import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { InternalTokenPayload, TokenService } from "../services/token.service";
import { adminService } from "../services/admin.service";
import { UserProfileAdminResponse } from "../types/user";
import { authService } from "../services/auth.service";

type RefreshFn = () => void | Promise<void>;

let refreshUserProfileFn: RefreshFn | null = null;

export function refreshUserProfile(): void {
  if (refreshUserProfileFn) {
    void Promise.resolve(refreshUserProfileFn());
  }
}

interface UserContextType {
  // User profile from API (detailed, may be null initially)
  userProfile: UserProfileAdminResponse | undefined;
  // Loading states
  loadingProfile: boolean;
  // Error state
  error: string | undefined;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

const REFRESH_TOKEN_CHECK_INTERVAL: number = 3 * 60 * 1000; // Every 3 minutes

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfileAdminResponse>();
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState<string>();

  useEffect((): void => {
    void loadUserProfile();
  }, []);

  useEffect(() => {
    refreshUserProfileFn = loadUserProfile;
    window.addEventListener("storage", handleStorageChange);
    return (): void => {
      refreshUserProfileFn = null;
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [loadingProfile]);

  useEffect(() => {
    if (!userProfile) {
      return;
    }

    checkTokenExpiryAndRefreshIfNeeded();

    const intervalId: number = setInterval((): void => {
      checkTokenExpiryAndRefreshIfNeeded();
    }, REFRESH_TOKEN_CHECK_INTERVAL);

    return (): void => clearInterval(intervalId);
  }, [userProfile]);

  function checkTokenExpiryAndRefreshIfNeeded(): void {
    const internalTokenPayload: InternalTokenPayload | null =
      TokenService.getInternalTokenPayload();

    const currentDate = new Date();
    const tokenExpMs: number = (internalTokenPayload?.exp || 0) * 1000;

    if (
      !tokenExpMs ||
      !internalTokenPayload?.role ||
      tokenExpMs < currentDate.getTime() || // already expired
      tokenExpMs > currentDate.getTime() + 10 * 60 * 1000 // expires in more than 10 min
    ) {
      return;
    }

    void authService.signIn(
      internalTokenPayload.role,
      internalTokenPayload.organizationId,
    );
  }

  function handleStorageChange(e: StorageEvent): void {
    // Token set in other tab
    if (e.key === TokenService.TOKEN_KEY && !!e.newValue) {
      return void loadUserProfile();
    }

    // Full storage or token cleared in other tab
    if ((!e.key || e.key === TokenService.TOKEN_KEY) && !e.newValue) {
      return void authService.logout();
    }
  }

  async function loadUserProfile(): Promise<void> {
    const isInternalTokenValid: boolean = TokenService.isInternalTokenValid();
    if (!isInternalTokenValid || loadingProfile) {
      return;
    }
    try {
      setLoadingProfile(true);
      setError(undefined);

      const profile: UserProfileAdminResponse =
        await adminService.getCurrentUserProfile();

      setUserProfile(profile);
    } catch (err: any) {
      const responseCode =
        err?.details?.code ?? err?.response?.data?.code ?? err?.code;
      if (responseCode === "ORG_ACCESS_REVOKED") {
        await authService.logout("org_access_changed");
        return;
      }
      setError("Failed to load user profile");
    } finally {
      setLoadingProfile(false);
    }
  }

  const value: UserContextType = { userProfile, loadingProfile, error };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
